import { inject, injectable } from 'tsyringe';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { LoggerService } from '../../logger.service';
import { kDITOWUtilsWrapper } from '../../../constants/injectionTokens';
import { IOWUtilsWrapper } from '../../../interfaces/ow-wrappers/ow-utils-wrapper.interface';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Global)
export class GlobalConditionCooldownFromInstall implements ICondition {
  private readonly logger = LoggerService.getCategory(
    'GlobalConditionCooldownFromInstall',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    @inject(kDITOWUtilsWrapper)
    private readonly owUtilsWrapper: IOWUtilsWrapper,
  ) {}

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(): Promise<MatchResult> {
    const configs = await this.datastoreService.getGlobalConfigurations();
    if (!configs) {
      this.logger.warn('Failed to get server configurations - returning false');
      return [false, NoRecommendationsReasons.FailedToGetServiceConfig];
    }

    const owInstallCooldownExpired: boolean =
      await this.isCooldownHrsFromInstallPassed(
        configs.cooldownFromOverwolfInstallationInHours,
      );

    if (!owInstallCooldownExpired) {
      return [false, NoRecommendationsReasons.CooldownInitial];
    }

    return [true, undefined];
  }

  // ---------------------------------------------------------------------------
  private async isCooldownHrsFromInstallPassed(
    cooldownHrs: number,
  ): Promise<boolean> {
    if (isNaN(cooldownHrs)) {
      return true;
    }

    const clientInfo = await this.owUtilsWrapper.getClientInfo();

    const timePassedInMs = Date.now() - clientInfo?.installTime;
    const timePassedHrs = timePassedInMs / (1000 * 60 * 60);

    const matches = timePassedHrs >= cooldownHrs;

    // eslint-disable-next-line max-len
    const msg = `Installed: ${Math.floor(
      timePassedHrs,
    )} hrs ago, config: ${cooldownHrs}hrs, ret:${matches}`;

    this.logger.info(msg);
    return matches;
  }
}
